/**
 * Centralized API client for Supabase Edge Functions
 * Provides a unified interface for calling Edge Functions from the frontend
 */

import { supabase } from "@/lib/supabase";

export class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
  }

  /**
   * Make a request to an Edge Function
   */
  private async request<T>(
    functionName: string,
    options: {
      method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
      body?: unknown;
      headers?: Record<string, string>;
      params?: Record<string, string>;
    } = {},
  ): Promise<T> {
    const { method = "GET", body, headers = {}, params } = options;

    // Get session for auth
    const {
      data: { session },
    } = (await supabase?.auth.getSession()) || { data: { session: null } };
    const authToken =
      session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Build URL with query parameters
    const url = new URL(`${this.baseUrl}/${functionName}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    try {
      const response = await fetch(url.toString(), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      // Handle streaming responses (e.g., maat-chat)
      if (response.headers.get("content-type")?.includes("text/event-stream")) {
        return response as unknown as T;
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || response.statusText };
        }
        console.error("API Error Response:", {
          status: response.status,
          statusText: response.statusText,
          errorData,
          url: url.toString(),
        });
        console.error("Full errorData:", JSON.stringify(errorData, null, 2));
        const errorMessage =
          errorData.error ||
          errorData.details ||
          errorData.message ||
          `API error: ${response.statusText}`;
        console.error("Error message:", errorMessage);
        throw new Error(errorMessage);
      }

      // Handle empty responses
      const contentType = response.headers.get("content-type");
      if (!contentType?.includes("application/json")) {
        return null as T;
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Network request failed");
    }
  }

  /**
   * GET request
   */
  async get<T>(
    functionName: string,
    params?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>(functionName, { method: "GET", params });
  }

  /**
   * POST request
   */
  async post<T>(
    functionName: string,
    body?: unknown,
    options?: { params?: Record<string, string> },
  ): Promise<T> {
    return this.request<T>(functionName, {
      method: "POST",
      body,
      params: options?.params,
    });
  }

  /**
   * POST request with query params (alias for convenience)
   */
  async postWithParams<T>(
    functionName: string,
    body?: unknown,
    options?: { params?: Record<string, string> },
  ): Promise<T> {
    return this.post<T>(functionName, body, options);
  }

  /**
   * PUT request
   */
  async put<T>(
    functionName: string,
    body?: unknown,
    options?: { params?: Record<string, string> },
  ): Promise<T> {
    return this.request<T>(functionName, {
      method: "PUT",
      body,
      params: options?.params,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    functionName: string,
    options?: { params?: Record<string, string> },
  ): Promise<T> {
    return this.request<T>(functionName, {
      method: "DELETE",
      params: options?.params,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(functionName: string, body?: unknown): Promise<T> {
    return this.request<T>(functionName, { method: "PATCH", body });
  }
}

export const apiClient = new ApiClient();
